import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { sanitizeRoleExceptions } from "@/helpers/jcRoles"
import type { AssortmentFilterValueResponse, JcRole } from "@/types/jcFilterSystem"

const httpService = new HttpService()

/** `{ filterOptionId: [role, ...] }` — only the categories that deviate. */
export type RoleExceptionMap = Record<string, JcRole[]>

interface JcAssortmentFilterValuesState {
  byProduct: Record<string, string[]>
  // Per-category role exceptions per product, mirroring the server's sparse map.
  rolesByProduct: Record<string, RoleExceptionMap>
}

export const useJcAssortmentFilterValuesStore = defineStore({
  id: "jc.assortment.filter.values",
  state: (): JcAssortmentFilterValuesState => ({
    byProduct: {},
    rolesByProduct: {},
  }),
  actions: {
    async fetch(assortmentProductId: string): Promise<string[]> {
      if (!assortmentProductId) return []
      try {
        const url = CONSTANT.API.JC_FILTER.ASSORTMENT_FILTER_VALUE.LIST.replace("{id}", assortmentProductId)
        const result = (await httpService.get(url)) as { data: AssortmentFilterValueResponse }
        const ids = result?.data?.filterOptionIds ?? []
        this.byProduct[assortmentProductId] = ids
        this.rolesByProduct[assortmentProductId] = sanitizeRoleExceptions(result?.data?.roles, ids)
        return ids
      } catch (error) {
        console.error(`Failed to fetch filter options for assortment product ${assortmentProductId}`, error)
        this.byProduct[assortmentProductId] = []
        this.rolesByProduct[assortmentProductId] = {}
        return []
      }
    },

    async fetchMany(assortmentProductIds: string[]): Promise<Record<string, string[]>> {
      const ids = assortmentProductIds.filter((id): id is string => !!id)
      const lists = await Promise.all(ids.map((id) => this.fetch(id)))
      const result: Record<string, string[]> = {}
      ids.forEach((id, i) => {
        result[id] = lists[i]
      })
      return result
    },

    /**
     * Replace-all: the complete desired tag list wins, the backend diffs.
     *
     * `roleExceptions` is deliberately three-valued, because the endpoint treats
     * an absent `roles` key and an empty one differently:
     *   undefined -> key omitted; the caller does not manage roles and the stored
     *                exceptions are carried over untouched (bulk tag edits).
     *   a map     -> the caller is authoritative; anything not in it is cleared.
     *                `{}` therefore wipes every exception — that is the only way
     *                to remove the last one, so it must stay expressible.
     * Never merge client-side: send the full desired map, same as the tag list.
     */
    async replace(assortmentProductId: string, filterOptionIds: string[], roleExceptions?: RoleExceptionMap): Promise<boolean> {
      if (!assortmentProductId) return false
      try {
        const url = CONSTANT.API.JC_FILTER.ASSORTMENT_FILTER_VALUE.REPLACE.replace("{id}", assortmentProductId)
        const managesRoles = roleExceptions !== undefined
        // Exceptions for options that are about to be untagged would be dropped
        // by the replace anyway — leave them out so client and server agree.
        const roles = managesRoles ? sanitizeRoleExceptions(roleExceptions, filterOptionIds) : undefined
        await httpService.put(url, managesRoles ? { filterOptionIds, roles } : { filterOptionIds })
        this.byProduct[assortmentProductId] = [...filterOptionIds]
        // Mirror the server: it carries an untouched exception over only for the
        // options that survive the replace, so untagged ones lose theirs here too.
        this.rolesByProduct[assortmentProductId] = managesRoles ? (roles as RoleExceptionMap) : sanitizeRoleExceptions(this.rolesByProduct[assortmentProductId], filterOptionIds)
        return true
      } catch (error) {
        console.error(`Failed to replace filter options for assortment product ${assortmentProductId}`, error)
        return false
      }
    },

    async replaceMany(updates: Record<string, string[]>, onProgress?: (done: number, total: number, currentId: string, success: boolean) => void): Promise<{ ok: string[]; failed: string[] }> {
      const entries = Object.entries(updates)
      const ok: string[] = []
      const failed: string[] = []
      const total = entries.length
      let done = 0
      // Sequential — keeps the backend from getting hammered and prevents
      // race conditions on the same configurator's M:N table.
      for (const [id, ids] of entries) {
        // Bulk edits touch tags only, so `roles` stays omitted and the
        // hand-maintained per-category exceptions survive untouched.
        const success = await this.replace(id, ids)
        done++
        if (success) ok.push(id)
        else failed.push(id)
        try {
          onProgress?.(done, total, id, success)
        } catch (e) {
          console.error("bulk progress callback failed", e)
        }
      }
      return { ok, failed }
    },

    get(assortmentProductId: string): string[] {
      return this.byProduct[assortmentProductId] ?? []
    },

    getRoleExceptions(assortmentProductId: string): RoleExceptionMap {
      return this.rolesByProduct[assortmentProductId] ?? {}
    },
  },
})
